import React, { Component } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

// Sep 2026 — Global render-error boundary. Wraps <App/> at the root
// (src/index.js) so ANY uncaught render/lifecycle error anywhere in the
// tree shows this fallback instead of a blank white screen (React's
// default behavior for an uncaught render error is to unmount the whole
// tree — with no boundary anywhere, that previously meant total blank).
//
// Root cause this was built for: submit "Buat Order Baru" / "Buat Invoice
// Baru" navigates to a lazy-loaded Detail page (OrderDetail/InvoiceDetail).
// If the browser tab had been open across a deploy, the JS chunk for that
// route can 404 (old hash, new deploy). lazyWithRetry (App.js) auto-reloads
// once; if THAT reload also lands on a stale cached index.html (Service
// Worker fallback) the same chunk 404s again — lazyWithRetry's second
// attempt throws with nothing to catch it. This boundary is that catch.
//
// Renders `this.props.children` unchanged whenever nothing has thrown —
// zero effect on normal app behavior by construction.
function isChunkLoadFailure(error) {
  if (!error) return false;
  // Tagged explicitly by lazyWithRetry's second (non-auto-recovering)
  // failure — most reliable signal, doesn't depend on browser-specific
  // error wording.
  if (error.isChunkLoadRetryFailure) return true;
  // Fallback heuristic for chunk failures that didn't go through
  // lazyWithRetry's tagging (defense in depth).
  if (error.name === "ChunkLoadError") return true;
  const msg = String(error.message || "");
  return /loading chunk|loading css chunk|reading 'call'/i.test(msg);
}

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error(
      "[ErrorBoundary] Unhandled render error:",
      error,
      "\nComponent stack:",
      errorInfo?.componentStack
    );
  }

  render() {
    if (this.state.hasError) {
      const chunkError = isChunkLoadFailure(this.state.error);
      return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-4">
          <div className="bg-white rounded-lg shadow-md p-8 max-w-md w-full text-center">
            <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {chunkError ? "Aplikasi Sudah Diperbarui" : "Terjadi Kesalahan"}
            </h2>
            <p className="text-gray-600 mb-4">
              {chunkError
                ? "Versi aplikasi sudah diperbarui. Silakan muat ulang halaman untuk melanjutkan."
                : "Terjadi kesalahan tak terduga saat menampilkan halaman ini. Data yang sudah tersimpan sebelumnya aman — silakan muat ulang halaman."}
            </p>
            <div className="flex flex-col gap-2">
              <Button onClick={() => window.location.reload()} className="w-full">
                <RefreshCw className="w-4 h-4 mr-2" />
                Muat Ulang Halaman
              </Button>
              <Button
                variant="outline"
                onClick={() => { window.location.href = "/"; }}
                className="w-full"
              >
                Kembali ke Beranda
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
